import { createContext, createMemo, useContext, type Accessor, type ParentComponent } from "solid-js";
import { createSortOrder } from "../../shared/hooks/createSortOrder";
import { createStore } from "solid-js/store";
import type { Voyage } from "../../shared/api/types";
import {
  applyFilters,
  emptyFilters,
  type SortDir,
  type SortEntry,
  type SortKey,
  type VesselsFilters,
  sortVoyages,
} from "./filtering";
import { defaultVesselsRange } from "./vessels-helpers";

export interface VesselsState {
  voyages: Voyage[];
  filters: VesselsFilters;
  selectedKey: string | null;
  isLoading: boolean;
  hasFetched: boolean;
  status: { text: string; tone: "info" | "loading" | "error" };
}

// Default multi-sort: by Commence date (newest first). Matches the old
// single-sort default; multi-column sort is opt-in from the column header.
const DEFAULT_SORT: SortEntry[] = [{ key: "commenceDate", dir: "desc" }];

function initialState(): VesselsState {
  const range = defaultVesselsRange();
  const filters = emptyFilters();
  // Default the Commence column to a 6-month window straddling today —
  // matches the old overlap-style default just narrower (only voyages
  // that commence in the window, not just those active in it).
  filters.commenceFrom = range.from;
  filters.commenceTo = range.to;
  return {
    voyages: [],
    filters,
    selectedKey: null,
    isLoading: false,
    hasFetched: false,
    status: { text: "", tone: "info" },
  };
}

export interface VesselsContextValue {
  state: VesselsState;
  filtered: () => Voyage[];
  // Multi-column sort exposed for the table headers.
  sortOrder: Accessor<SortEntry[]>;
  setSortOrder: (next: SortEntry[]) => void;
  // Per-column conveniences mirroring createSortOrder in settings.
  isSortActive: (key: SortKey) => Accessor<boolean>;
  sortDirOf: (key: SortKey) => Accessor<SortDir>;
  sortIndexOf: (key: SortKey) => Accessor<number | null>;
  setSort: (key: SortKey, dir: SortDir) => void;
  removeSort: (key: SortKey) => void;
  // Distinct dropdown values derived from the loaded voyages.
  distinctStatuses: () => string[];
  distinctTradeAreas: () => string[];
  distinctLobs: () => string[];
  distinctOprTypes: () => string[];
  // Mutations
  setVoyages: (voyages: Voyage[]) => void;
  setFilter: <K extends keyof VesselsFilters>(key: K, value: VesselsFilters[K]) => void;
  setFilters: (next: Partial<VesselsFilters>) => void;
  setSelectedKey: (key: string | null) => void;
  setLoading: (active: boolean) => void;
  setHasFetched: (v: boolean) => void;
  setStatus: (text: string, tone: VesselsState["status"]["tone"]) => void;
}

const VesselsContext = createContext<VesselsContextValue>();

export const VesselsProvider: ParentComponent = (props) => {
  const [state, setState] = createStore<VesselsState>(initialState());
  // Shared multi-column sort hook — same behaviour as the Settings
  // grids (priority badges, append-at-end for new columns).
  const sort = createSortOrder<SortKey>(DEFAULT_SORT);
  const sortOrder = sort.order;

  // Sort-then-filter, split into two memos: a filter keystroke re-runs
  // only the O(n) filter over the pre-sorted list instead of paying the
  // O(n log n) sort every time. Filtering preserves order, so the
  // output is identical.
  const sorted = createMemo(() => sortVoyages(state.voyages, sortOrder()));
  const filtered = createMemo(() => applyFilters(sorted(), state.filters));

  function distinctOf(key: keyof Voyage): () => string[] {
    return createMemo(() => {
      const seen = new Set<string>();
      for (const v of state.voyages) {
        const raw = v[key];
        if (typeof raw === "string" && raw) seen.add(raw);
      }
      return [...seen].sort((a, b) => a.localeCompare(b));
    });
  }

  const distinctStatuses = distinctOf("voyageStatus");
  const distinctTradeAreas = distinctOf("tradeArea");
  const distinctLobs = distinctOf("lob");
  const distinctOprTypes = distinctOf("oprType");

  const value: VesselsContextValue = {
    state,
    filtered,
    sortOrder,
    setSortOrder: sort.replace,
    isSortActive: sort.isActive,
    sortDirOf: sort.dirOf,
    sortIndexOf: sort.indexOf,
    setSort: sort.setSort,
    removeSort: sort.removeSort,
    distinctStatuses,
    distinctTradeAreas,
    distinctLobs,
    distinctOprTypes,
    setVoyages: (voyages) => setState("voyages", voyages),
    setFilter: (key, value) => setState("filters", key, value as VesselsFilters[typeof key]),
    setFilters: (next) => setState("filters", (prev) => ({ ...prev, ...next })),
    setSelectedKey: (key) => setState("selectedKey", key),
    setLoading: (active) => setState("isLoading", active),
    setHasFetched: (v) => setState("hasFetched", v),
    setStatus: (text, tone) => setState("status", { text, tone }),
  };

  return (
    <VesselsContext.Provider value={value}>
      {props.children}
    </VesselsContext.Provider>
  );
};

export function useVessels(): VesselsContextValue {
  const ctx = useContext(VesselsContext);
  if (!ctx) throw new Error("useVessels must be used inside VesselsProvider");
  return ctx;
}

export { DEFAULT_SORT };
